import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { AgencyResponse } from '../../../services/api/types.gen';

export interface CreateAgencyDialogProps {
  open: boolean;
  onClose: () => void;
  existingAgencies: AgencyResponse[];
  onCreate: (data: {
    system_name: string;
    written_name: string;
    abbreviation: string;
    is_kinnser_enabled: boolean;
    add_onboarding_team: boolean;
    sync_clinic_info: boolean;
  }) => Promise<void>;
}

const EMPTY_FORM = {
  /** Submitted as both `system_name` and `written_name` so they always match. */
  agency_name: '',
  abbreviation: '',
  is_kinnser_enabled: false,
  add_onboarding_team: true,
  sync_clinic_info: true,
};

const CreateAgencyDialog: React.FC<CreateAgencyDialogProps> = ({
  open,
  onClose,
  existingAgencies,
  onCreate,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!open) setForm(EMPTY_FORM);
  }, [open]);

  const isValid = form.agency_name.trim() && form.abbreviation.trim();

  const duplicateFieldErrors = useMemo(() => {
    const agencyName = form.agency_name.trim();
    const abbreviation = form.abbreviation.trim();
    const errors: Partial<Record<'agency_name' | 'abbreviation', string>> = {};
    for (const a of existingAgencies) {
      if (
        agencyName &&
        (a.system_name === agencyName || a.written_name === agencyName)
      ) {
        errors.agency_name = 'An agency with this name already exists.';
      }
      if (abbreviation && a.abbreviation === abbreviation) {
        errors.abbreviation =
          'An agency with this abbreviation already exists.';
      }
    }
    return errors;
  }, [existingAgencies, form.agency_name, form.abbreviation]);

  const hasDuplicateConflict = Object.keys(duplicateFieldErrors).length > 0;

  const handleCreate = async () => {
    if (!isValid || hasDuplicateConflict) return;
    setIsCreating(true);
    try {
      const name = form.agency_name.trim();
      await onCreate({
        system_name: name,
        written_name: name,
        abbreviation: form.abbreviation.trim(),
        is_kinnser_enabled: form.is_kinnser_enabled,
        add_onboarding_team: form.add_onboarding_team,
        // Only a Kinnser agency has clinic info to pull.
        sync_clinic_info: form.is_kinnser_enabled && form.sync_clinic_info,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const textField = (
    key: 'agency_name' | 'abbreviation',
    label: string,
    placeholder: string
  ) => (
    <div className='space-y-1.5'>
      <Label htmlFor={`agency-${key}`}>{label}</Label>
      <Input
        id={`agency-${key}`}
        placeholder={placeholder}
        value={form[key]}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        aria-invalid={Boolean(duplicateFieldErrors[key])}
      />
      {duplicateFieldErrors[key] ? (
        <p className='text-xs text-destructive'>{duplicateFieldErrors[key]}</p>
      ) : null}
    </div>
  );

  const afterCreationOptions = (
    [
      {
        id: 'agency-add-onboarding-team',
        key: 'add_onboarding_team' as const,
        title: 'Add onboarding team',
        description:
          'Invite support and intake staff with standard admin roles.',
      },
      {
        id: 'agency-sync-clinic-info',
        key: 'sync_clinic_info' as const,
        title: 'Sync clinic info',
        description: 'Pull clinics and insurances from RogerDoc clinic info.',
      },
    ] as const
  )
    .filter(
      option => option.key !== 'sync_clinic_info' || form.is_kinnser_enabled
    )
    .map(option => ({
      ...option,
      checked: form[option.key],
      onToggle: (checked: boolean | 'indeterminate') =>
        setForm(prev => ({
          ...prev,
          [option.key]: checked === true,
        })),
    }));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>Create agency</DialogTitle>
        </DialogHeader>

        <div className='py-2 space-y-3'>
          {textField(
            'agency_name',
            'Agency name  (RogerDocs agency_id)',
            'Acme Home Health'
          )}
          {textField('abbreviation', 'Abbreviation', 'AHH')}

          <div className='space-y-1.5'>
            <Label>Integrations</Label>
            <div className='flex gap-2'>
              <button
                type='button'
                onClick={() =>
                  setForm(prev => ({
                    ...prev,
                    is_kinnser_enabled: !prev.is_kinnser_enabled,
                  }))
                }
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                  form.is_kinnser_enabled
                    ? 'bg-primary/10 border-primary/40 text-foreground'
                    : 'border-input hover:bg-muted/50 text-muted-foreground'
                }`}
              >
                <div
                  className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                    form.is_kinnser_enabled
                      ? 'bg-primary border-primary'
                      : 'border-input bg-background'
                  }`}
                >
                  {form.is_kinnser_enabled && (
                    <svg
                      className='w-2.5 h-2.5 text-primary-foreground'
                      viewBox='0 0 10 10'
                      fill='none'
                    >
                      <path
                        d='M1.5 5L4 7.5L8.5 2.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
                Kinnser
              </button>
            </div>
            <p className='text-xs text-muted-foreground'>
              RogerEMR is turned on from the Products page in Docs internal
              tools.
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label>After creation</Label>
            <div className='rounded-lg border divide-y overflow-hidden'>
              {afterCreationOptions.map(
                ({ id, key, title, description, checked, onToggle }) => (
                  <label
                    key={key}
                    htmlFor={id}
                    className='grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-2.5 gap-y-0 py-2 px-2.5 cursor-pointer hover:bg-muted/40 transition-colors'
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={onToggle}
                      checkboxSize='sm'
                      className='row-span-2 self-center'
                    />
                    <span className='text-sm font-medium leading-tight'>
                      {title}
                    </span>
                    <p className='text-xs text-muted-foreground leading-snug'>
                      {description}
                    </p>
                  </label>
                )
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !isValid || hasDuplicateConflict}
          >
            {isCreating && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
            Create agency
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAgencyDialog;
